/**
 * The TWL pipeline across all of the provider's buildings (provider Renata Silva, 2026-10-23).
 *
 * Mirrors backend/src/routes/emergeDaySheetTwl.js (GET /emerge/day-sheet/twl, /twl/export) and
 * services/emerge/twlPipeline.js; docs/provider/TWL_PIPELINE.md. Residents are named only where the provider is
 * care-related; elsewhere the server sends a count (`withheld`) and no row. Read-only.
 *
 * Derived signals only (2026-10-26, until Isaiah rules): no PA name and no note date ever arrive here, only the PA
 * activity bucket. Every filter is applied by the server; the list is paged; the CSV is every matching row.
 */
import { api, apiDownloadFile } from "../../../services/api";
import type { BuildingClosure } from "../providerApi";
import type { TwlRemind } from "./paReminderApi";

export type TwlStage =
  | "DISPENSED" | "ATD_APPROVED" | "DCF_APPROVED" | "UMED_SUBMITTED" | "UMED_IN_PROGRESS" | "UMED_UNRECOGNISED" | "ENROLLED"
  | "PREPARING_FOR_DCF" | "PA_IN_PROGRESS" | "WITH_PA_NO_NOTE" | "PA_NOTED" | "ON_HOLD" | "CLOSED_OUT" | "WAITING_FOR_PA"
  | "OPEN_OFF_QUEUE" | "STATUS_UNRECOGNISED" | "NO_OPEN_CASE";
export type TwlAge = "LT_30" | "D30_90" | "D90_365" | "GT_365" | "UNKNOWN";
export type TwlAgeFilter = TwlAge | "BEFORE_2025";
export type TwlActivity = "NONE" | "D0_7" | "D8_30" | "D31_90" | "OVER_90" | "NA";
export type TwlTestAge = "OVER_1Y" | "WITHIN_1Y" | "NOT_RECORDED";

export const TWL_AGES: TwlAge[] = ["LT_30", "D30_90", "D90_365", "GT_365", "UNKNOWN"];
export const PAGE_SIZE = 200;

export interface TwlCount { count: number; withheld: number }
export interface TwlStageInfo extends TwlCount { key: TwlStage; label: string; what: string }
export interface TwlAgeInfo extends TwlCount { key: TwlAge; label: string }
export interface TwlActivityInfo extends TwlCount { key: TwlActivity; label: string }

export interface TwlBuilding {
  id: string;
  name: string | null;
  city: string | null;
  state: string | null;
  closure: BuildingClosure | null;
  total: number;
  never_followed_up: number;
  earlier_case_closed: number;
  over_1y: number;
  before_2025: number;
  withheld: number;
  stages: Record<TwlStage, number>;
}

export interface TwlRow {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  facility_id: string;
  building: string | null;
  building_closed: boolean;
  stage: TwlStage;
  stage_label: string;
  /** When a PA last wrote a note on the deciding case, as a bucket. Never a date, never a name. */
  pa_activity: TwlActivity;
  pa_activity_label: string;
  age: TwlAge;
  age_label: string;
  age_source: string | null;
  before_2025: boolean;
  test_age: TwlTestAge;
  test_age_label: string;
  earlier_case_closed: boolean;
  /** Remind PA (2026-10-27, docs/provider/REMIND_PA.md): may this provider remind the PA, and their own state. Absent on an older server. */
  remind?: TwlRemind;
}

export interface TwlPipeline {
  today: string;
  stages: TwlStageInfo[];
  ages: TwlAgeInfo[];
  activities: TwlActivityInfo[];
  before_2025: TwlCount;
  test_over_1y: TwlCount;
  matrix: Record<TwlStage, Record<TwlAge, number>>;
  total: number;
  withheld: number;
  closed: { buildings: number; residents: number; withheld: number };
  /** Beside the pipeline: TWL without Qualified; provider-tag TWL Q with no CounselEAR TWL or Qualified yet; provider-tag TWL Q where CounselEAR records another outcome. */
  not_counted: { twl_not_qualified: number; provider_tags_only: number; provider_tags_disagree: number };
  buildings: TwlBuilding[];
  rows: TwlRow[];
  rows_total: number;
  offset: number;
  limit: number | null;
  truncated: boolean;
  scope_buildings: number;
  enterprise: boolean;
}

interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  status?: number;
}

export interface TwlFilters {
  stage: TwlStage | "";
  age: TwlAgeFilter | "";
  activity: TwlActivity | "";
  facility: string;
  includeClosed: boolean;
}
export const NO_FILTERS: TwlFilters = { stage: "", age: "", activity: "", facility: "", includeClosed: false };

/** The query the server reads, for the page and the export alike. */
export function filterParams(f: TwlFilters): Record<string, string> {
  const p: Record<string, string> = { include_closed: f.includeClosed ? "true" : "false" };
  if (f.stage) p.stage = f.stage;
  if (f.age) p.age = f.age;
  if (f.activity) p.activity = f.activity;
  if (f.facility) p.facility = f.facility;
  return p;
}

export const twlPipelineApi = {
  /** Every count, and one page of the named rows the filters select. */
  get: async (f: TwlFilters, offset = 0): Promise<ApiResult<TwlPipeline>> => {
    const qp = new URLSearchParams({ ...filterParams(f), offset: String(offset), limit: String(PAGE_SIZE) });
    return (await api.get(`/emerge/day-sheet/twl?${qp.toString()}`)) as ApiResult<TwlPipeline>;
  },
  /** Every named row the filters select, as CSV. Resolves to an error message, or null when the file was saved. */
  exportCsv: (f: TwlFilters): Promise<string | null> => apiDownloadFile("/emerge/day-sheet/twl/export", filterParams(f)),
};
